import { projectCacheAtom } from "state-atoms";
import { Project, ProjectCache } from "shared/datamodel/schemas/project";
import { createProjectNew, deleteProjectNew, editProjectNew, getProjectsByIds } from "frontend/api";
import { useGenericCache } from "./use-general-cache";
import { useTeamsWithProjects } from "./use-teams-with-projects";
import { useCallback } from "react";
import { useBoardsSections } from "./use-boards-sections";
import { BoardSectionType, getSectionId } from "shared/datamodel/schemas/board-section";

export function useProjectsCache() {
  const { addProject, updateProject, deleteProject } = useTeamsWithProjects();
  const { filterDeletedBoardsFromInitialSections } = useBoardsSections();
  const { getItems, isLoading, createItemCache, removeItemCache, updateItemCache } = useGenericCache<
    Project,
    ProjectCache,
    "documentId",
    { name: string; teamId: string }
  >({
    cacheAtom: projectCacheAtom,
    fetchByIds: getProjectsByIds,
    entityName: "projects",
    entityKey: "project",
    keyField: "documentId",
    createItem: async (item: { name: string; teamId: string }) => {
      const project = await createProjectNew(item);
      addProject(project.teamDocumentId, project);
      return project;
    },
    removeItem: async (item: Partial<Project>) => {
      if (item.documentId && item.teamDocumentId) {
        filterDeletedBoardsFromInitialSections(getSectionId(BoardSectionType.PROJECT, item.documentId));
        await deleteProjectNew(item.documentId);
        deleteProject(item.teamDocumentId, item.documentId);
        return true;
      }
      return false;
    },
    updateItem: async (item: Partial<Project>) => {
      if (!item.teamDocumentId || !item.documentId) {
        throw new Error("Team ID and document ID are required");
      }
      const project = await editProjectNew({ ...item, documentId: item.documentId });
      updateProject(item.teamDocumentId, item.documentId, item);
      if (!project) throw new Error("Failed to update team");
      return project;
    },
  });

  const leaveProject = useCallback(
    async (item: Partial<Project>) => {
      if (item.documentId && item.teamDocumentId && (await updateItemCache(item))) {
        deleteProject(item.teamDocumentId, item.documentId);
      }
    },
    [deleteProject, updateItemCache]
  );

  return {
    getProjects: getItems,
    isLoading,
    createProject: createItemCache,
    removeProject: removeItemCache,
    updateProject: updateItemCache,
    leaveProject,
  };
}
